import type { Point, PolygonPath } from '@/model/types'

/** Polygon centroid (area-weighted). Falls back to vertex average for degenerate input. */
export function centroid(poly: PolygonPath): Point {
  const n = poly.length
  if (n === 0) return { x: 0, y: 0 }
  if (n < 3) {
    return {
      x: poly.reduce((s, p) => s + p.x, 0) / n,
      y: poly.reduce((s, p) => s + p.y, 0) / n,
    }
  }
  let a = 0
  let cx = 0
  let cy = 0
  for (let i = 0; i < n; i++) {
    const p0 = poly[i]
    const p1 = poly[(i + 1) % n]
    const cross = p0.x * p1.y - p1.x * p0.y
    a += cross
    cx += (p0.x + p1.x) * cross
    cy += (p0.y + p1.y) * cross
  }
  a *= 0.5
  if (Math.abs(a) < 1e-9) {
    return {
      x: poly.reduce((s, p) => s + p.x, 0) / n,
      y: poly.reduce((s, p) => s + p.y, 0) / n,
    }
  }
  return { x: cx / (6 * a), y: cy / (6 * a) }
}

/** Signed polygon area (positive for counter-clockwise in screen coords). */
export function signedArea(poly: PolygonPath): number {
  let a = 0
  for (let i = 0; i < poly.length; i++) {
    const p0 = poly[i]
    const p1 = poly[(i + 1) % poly.length]
    a += p0.x * p1.y - p1.x * p0.y
  }
  return a * 0.5
}

/** Absolute polygon area in square feet. */
export function area(poly: PolygonPath): number {
  return Math.abs(signedArea(poly))
}

/** Axis-aligned bounding box. */
export function bounds(poly: PolygonPath) {
  const xs = poly.map((p) => p.x)
  const ys = poly.map((p) => p.y)
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  }
}

/** Point-in-polygon (ray casting). */
export function pointInPolygon(pt: Point, poly: PolygonPath): boolean {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]
    const b = poly[j]
    const intersects =
      a.y > pt.y !== b.y > pt.y &&
      pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x
    if (intersects) inside = !inside
  }
  return inside
}

export function rectPath(x: number, y: number, w: number, h: number): PolygonPath {
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ]
}

/** Flatten a polygon to the [x0,y0,x1,y1,...] form Konva's Line expects. */
export function toFlatPoints(poly: PolygonPath): number[] {
  const out: number[] = []
  for (const p of poly) {
    out.push(p.x, p.y)
  }
  return out
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/** Closest point on segment AB to P, plus the parametric t in [0,1]. */
function closestOnSegment(p: Point, a: Point, b: Point) {
  const abx = b.x - a.x
  const aby = b.y - a.y
  const len2 = abx * abx + aby * aby
  let t = len2 === 0 ? 0 : ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2
  t = Math.max(0, Math.min(1, t))
  return { point: { x: a.x + t * abx, y: a.y + t * aby }, t }
}

/**
 * Closest point on a polygon's boundary to `pt`. Returns the point, the index of the
 * edge it lies on (vertex i → i+1), and the distance — used to insert a new corner.
 */
export function closestPointOnPolygon(pt: Point, poly: PolygonPath) {
  let best = { point: poly[0], edgeIndex: 0, dist: Infinity }
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]
    const b = poly[(i + 1) % poly.length]
    const { point } = closestOnSegment(pt, a, b)
    const d = distance(pt, point)
    if (d < best.dist) best = { point, edgeIndex: i, dist: d }
  }
  return best
}
